import { Injectable } from '@angular/core';
import { LoggedInUser } from '../../models/logged-in-user';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clarity: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class MicrosoftClarityService {
  static readonly DAILY_SESSION_STORAGE_KEY = 'clarity_daily_sessions';

  private currentUserId: string | undefined;
  private usageTrackingInitialized = false;
  private readonly sessionCountedDateKey = 'clarity_session_counted_date';

  private isClarityAvailable(): boolean {
    return (
      typeof window !== 'undefined' && typeof window.clarity === 'function'
    );
  }

  identifyUser(user: LoggedInUser): void {
    if (!this.isClarityAvailable() || !user) {
      return;
    }

    // Set user identification using Clarity's identify method
    const userId = user.username || user.email || 'unknown';
    this.currentUserId = userId;

    // Do not send the user's plain-text display name. Clarity hashes custom IDs.
    window.clarity('identify', userId);
  }

  /**
   * Clarity measures session duration natively. This adds the missing daily
   * visit ordinal so sessions can be segmented into first and repeat visits.
   */
  initializeUsageTracking(): void {
    if (this.usageTrackingInitialized || !this.isClarityAvailable()) {
      return;
    }

    this.usageTrackingInitialized = true;
    const today = this.getLocalDateKey();
    const alreadyCounted = this.readSessionCountedDate();
    const stored = this.readDailySessionCount();
    let sessionNumberToday = stored.date === today ? stored.count : 0;
    const isNewSession = alreadyCounted !== today;

    if (isNewSession) {
      sessionNumberToday++;
      this.writeDailySessionCount(today, sessionNumberToday);
      try {
        sessionStorage.setItem(this.sessionCountedDateKey, today);
      } catch {
        // Analytics must not interrupt the app when browser storage is blocked.
      }
    }

    // A reload keeps its sessionStorage marker and is not counted as a return.
    sessionNumberToday = Math.max(sessionNumberToday, 1);
    this.sendClarityTag('session_number_today', String(sessionNumberToday));
    this.sendClarityTag(
      'repeat_session_today',
      sessionNumberToday > 1 ? 'true' : 'false',
    );
    if (isNewSession) {
      this.sendClarityEvent('deeproots_session_started');
    }
  }

  trackLogout(): void {
    this.sendClarityEvent('user_logout');
  }

  clearIdentity(): void {
    this.trackLogout();
    this.currentUserId = undefined;
  }

  getCurrentUserId(): string | undefined {
    return this.currentUserId;
  }
  sendClarityTag(key: string, value: string | string[]): void {
    if (this.isClarityAvailable()) {
      window.clarity('set', key, value);
    }
  }

  sendClarityEvent(eventName: string): void {
    if (this.isClarityAvailable()) {
      window.clarity('event', eventName);
    }
  }

  trackEvent(
    eventName: string,
    tags: Record<string, string | string[]> = {},
  ): void {
    Object.entries(tags).forEach(([key, value]) => {
      this.sendClarityTag(key, value);
    });
    this.sendClarityEvent(eventName);
  }

  private getLocalDateKey(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private readSessionCountedDate(): string | null {
    try {
      return sessionStorage.getItem(this.sessionCountedDateKey);
    } catch {
      return null;
    }
  }

  private readDailySessionCount(): { date: string; count: number } {
    try {
      const stored = localStorage.getItem(
        MicrosoftClarityService.DAILY_SESSION_STORAGE_KEY,
      );
      if (stored) {
        const parsed = JSON.parse(stored) as {
          date?: unknown;
          count?: unknown;
        };
        if (
          typeof parsed.date === 'string' &&
          typeof parsed.count === 'number' &&
          Number.isFinite(parsed.count)
        ) {
          return { date: parsed.date, count: parsed.count };
        }
      }
    } catch {
      // Fall back to a first session when storage is blocked or malformed.
    }

    return { date: '', count: 0 };
  }

  private writeDailySessionCount(date: string, count: number): void {
    try {
      localStorage.setItem(
        MicrosoftClarityService.DAILY_SESSION_STORAGE_KEY,
        JSON.stringify({ date, count }),
      );
    } catch {
      // Analytics must not interrupt the app when browser storage is blocked.
    }
  }
}
